import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TableContainer,
  Typography,
} from '@mui/material';
import SimpleDialog from '../../../../common-components/dialog/SimpleDialog';
import {
  DeleteMyFleetVesselsResponse,
  MyFleetDialogStatus,
  MyFleetVessels,
} from '../../../../models/my-fleet.model';
import MyFleetVesselErrorTable from '../../my-fleet-vessel-error-table/my-fleet-vessel-error-table';
import MyFleetVesselConfirmationTable from '../my-fleet-vessel-confirmation-table/my-fleet-vessel-confirmation-table';

interface DeleteMyFleetVesselsProps {
  open: boolean;
  onCancel: () => void;
  onClose: () => void;
  onConfirm: () => void;
  status: MyFleetDialogStatus;
  response: DeleteMyFleetVesselsResponse | null;
  vessels: MyFleetVessels | null;
  batchProgress?: {
    completed: number;
    total: number;
    failed: number;
  };
  batchErrors?: any[];
}

function getDeleteProgressMessage(
  batchProgress: { completed: number; total: number } | undefined,
  vessels: MyFleetVessels
) {
  if (batchProgress && batchProgress.total > 0) {
    return `Deleting vessels: ${batchProgress.completed} of ${batchProgress.total}`;
  }
  return vessels.length > 1 ? 'Deleting vessels:' : 'Deleting vessel...';
}

function MyFleetVesselsDeleteDialog({
  open,
  onCancel,
  onClose,
  onConfirm,
  status,
  response,
  vessels,
  batchProgress,
  batchErrors,
}: DeleteMyFleetVesselsProps) {
  const CONTENT_MIN_HEIGHT = 80;
  const CONTENT_MAX_HEIGHT = 400;
  const CONTENT_MAX_WIDTH = 500;
  const TABLE_MIN_HEIGHT = 40;
  const TABLE_MAX_HEIGHT = 340;
  const isResponseArray = Array.isArray(response) && response.length > 0;

  if (!vessels) return null;

  const renderTitle = () => {
    switch (status) {
      case MyFleetDialogStatus.Error:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelOutlined sx={{ color: 'error.main' }} />
            {isResponseArray ? (
              <Typography>Errors raised during vessel deletion:</Typography>
            ) : (
              <Typography>An error occurred during vessel deletion.</Typography>
            )}
          </Box>
        );
      case MyFleetDialogStatus.Success:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutline sx={{ color: 'secondary.main' }} />
            <Typography data-testid="delete-vessels-success-dialog-content">
              {response as string}
            </Typography>
          </Box>
        );
      case MyFleetDialogStatus.Loading:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} sx={{ color: 'secondary.main' }} />
            <Typography data-testid="delete-vessels-dialog-loading">
              {getDeleteProgressMessage(batchProgress, vessels)}
            </Typography>
          </Box>
        );
      case MyFleetDialogStatus.Idle:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography data-testid="delete-vessels-dialog-content">
              {vessels.length > 1
                ? 'Are you sure you wish to delete these vessels?'
                : 'Are you sure you wish to delete this vessel?'}
            </Typography>
          </Box>
        );
      default:
        return <Typography>Delete my fleet vessels</Typography>;
    }
  };

  const renderActions = () => (
    <Stack
      direction="column"
      spacing={1}
      justifyContent={
        status === MyFleetDialogStatus.Loading ? 'flex-end' : 'space-between'
      }
      sx={{ width: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {status === MyFleetDialogStatus.Error && (
          <Typography sx={{ alignItems: 'flex-start', width: '100%' }}>
            {vessels.length > 1
              ? 'Not all vessels have been deleted.'
              : 'This vessel has not been deleted.'}
          </Typography>
        )}
        <Button
          variant={status !== MyFleetDialogStatus.Idle ? undefined : 'outlined'}
          onClick={status !== MyFleetDialogStatus.Idle ? onClose : onCancel}
          data-testid="delete-vessels-dialog-close-button"
          disabled={status === MyFleetDialogStatus.Loading}
          sx={{ mb: 0, minWidth: '80px' }}
        >
          {status !== MyFleetDialogStatus.Idle ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={
            status === MyFleetDialogStatus.Loading ||
            status === MyFleetDialogStatus.Success ||
            status === MyFleetDialogStatus.Error
          }
          data-testid="delete-vessel-deletion-confirm-button"
          variant="contained"
          color="error"
        >
          Confirm
        </Button>
      </Box>
    </Stack>
  );

  const renderContent = () => (
    <Box
      sx={{
        minHeight: CONTENT_MIN_HEIGHT,
        maxHeight: CONTENT_MAX_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexGrow: 1,
          mt: 1,
          mb: 1,
        }}
      >
        {renderTitle()}
      </Box>
      {status === MyFleetDialogStatus.Idle && (
        <TableContainer
          sx={{
            minHeight: `${TABLE_MIN_HEIGHT}px`,
            maxHeight: `${TABLE_MAX_HEIGHT}px`,
            mb: 1,
            overflow: 'auto',
            scrollbarWidth: 'thin',
          }}
        >
          <MyFleetVesselConfirmationTable vessels={vessels} />
        </TableContainer>
      )}
      {status === MyFleetDialogStatus.Loading &&
        batchErrors &&
        batchErrors.length > 0 && (
          <TableContainer
            sx={{
              minHeight: `${TABLE_MIN_HEIGHT}px`,
              maxHeight: `${TABLE_MAX_HEIGHT}px`,
              mt: 1,
              mb: 2,
              overflow: 'auto',
              scrollbarWidth: 'thin',
            }}
          >
            <MyFleetVesselErrorTable errors={batchErrors} vessels={vessels} />
          </TableContainer>
        )}
      {status !== MyFleetDialogStatus.Loading &&
        status === MyFleetDialogStatus.Error &&
        isResponseArray && (
          <TableContainer
            sx={{
              minHeight: `${TABLE_MIN_HEIGHT}px`,
              maxHeight: `${TABLE_MAX_HEIGHT}px`,
              mt: 1,
              mb: 2,
              overflow: 'auto',
              scrollbarWidth: 'thin',
            }}
          >
            <MyFleetVesselErrorTable
              errors={response || []}
              vessels={vessels}
            />
          </TableContainer>
        )}
    </Box>
  );

  return (
    <SimpleDialog
      onClose={onClose}
      open={open}
      title={vessels.length > 1 ? 'Delete Vessels' : 'Delete Vessel'}
      width={CONTENT_MAX_WIDTH}
      actions={renderActions()}
      data-testid="delete-vessels-dialog"
      disableBackdropClick
    >
      {renderContent()}
    </SimpleDialog>
  );
}

MyFleetVesselsDeleteDialog.defaultProps = {
  batchProgress: undefined,
  batchErrors: undefined,
};
export default MyFleetVesselsDeleteDialog;
